import React from 'react';

import service from './service';
import { Vendor } from './model';

export default function Login({ setVendor }) {
  const [netId, setNetId] = React.useState<string>('');
  const [email, setEmail] = React.useState<string>('');
  const [inputValue, setInputValue] = React.useState<string>('');
  const [vendorDialogVisible, setVendorDialogVisible] = React.useState(false);

  const submitCodeRequest = async (e) => {
    e.preventDefault();

    const vendorExists = await service.vendorExists(inputValue);
    if (vendorExists) {
      await requestCode(inputValue);
    } else {
      setVendorDialogVisible(true);
    }
  };

  const processCode = (e) => {
    e.preventDefault();

    (async () => {
      try {
        const vendor = await service.authenticate(netId, inputValue);
        setVendor(vendor);
      } catch (error) {
        alert(error.message || 'An error occurred during authentication.');
      }
    })();
  };

  async function createVendor(vendor: Vendor) {
    const addedVendor = await service.addVendor({ ...vendor, id: inputValue });
    await requestCode(addedVendor.id);
    setVendorDialogVisible(false);
  }

  async function requestCode(id: string) {
    try {
      setEmail(await service.requestCode(id));
      setNetId(id);
      setInputValue('');
    } catch (error) {
      alert(error.message || 'An unexpected error occurred.');
    }
  }

  function obfuscateEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    const obsfucatedLocalPart = localPart.length > 3 ? `${localPart.slice(0, 3)}...` : localPart;
    return `${obsfucatedLocalPart}@${domain}`;
  }

  return (
    <div>
      {!netId ? (
        <div className="flex items-start justify-center">
          <form onSubmit={submitCodeRequest} className="bg-white p-8 rounded shadow-md flex flex-col gap-4 w-80 my-6">
            <label htmlFor="login" className="text-gray-700 font-semibold">
              Login
            </label>
            <input id="login" type="text" value={inputValue} placeholder="Enter your BYU Net ID" onChange={(e) => setInputValue(e.target.value)} className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <button disabled={!inputValue} type="submit" className="bg-blue-600 text-white rounded px-4 py-2 font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed">
              Get code
            </button>
          </form>
        </div>
      ) : (
        <div className="flex items-start justify-center">
          <form onSubmit={processCode} className="bg-white p-8 rounded shadow-md flex flex-col gap-4 w-80 my-6">
            <label htmlFor="login" className="text-gray-700 font-semibold">
              Authenticate code
            </label>
            <div className="text-sm text-gray-500">
              Provide the code sent to <b>{obfuscateEmail(email)}</b>.
            </div>
            <input id="login" type="text" value={inputValue} placeholder="Code" onChange={(e) => setInputValue(e.target.value)} className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <button disabled={!inputValue} type="submit" className="bg-blue-600 text-white rounded px-4 py-2 font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed">
              Validate code
            </button>
          </form>
        </div>
      )}
      {vendorDialogVisible && <NewVendorDialog setShowVendorDialog={setVendorDialogVisible} createVendor={createVendor} />}
    </div>
  );
}

function NewVendorDialog({ setShowVendorDialog, createVendor }) {
  const [vendor, setVendor] = React.useState({
    name: '',
    email: '',
    phone: '',
    gitHubUrl: '',
    website: '',
  });

  const handleChange = (field: keyof typeof vendor) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setVendor((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createVendor(vendor);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 overflow-y-auto">
      <dialog id="vendorDialog" open className="rounded shadow-md p-6 bg-white max-w-md w-full mt-8 sm:mt-16 mx-auto">
        <form method="dialog" className="flex flex-col gap-4 w-full" onSubmit={handleSubmit}>
          <h2 className="text-lg font-semibold mb-2">Create Vendor Account</h2>
          <p className="text-sm">This information is used by your peers to contact you for collaborative activities. Please add accurate information for accounts that you frequently check.</p>
          <label className="flex flex-col">
            Name
            <input type="text" value={vendor.name} onChange={handleChange('name')} required className="border border-gray-300 rounded px-3 py-2 mt-1 font-normal" placeholder="Your real name" />
          </label>
          <label className="flex flex-col">
            Email <span className="text-sm italic text-gray-300">That you check frequently</span>
            <input type="email" value={vendor.email} onChange={handleChange('email')} required className="border border-gray-300 rounded px-3 py-2 mt-1" placeholder="xxxx@xxx.xxx" />
          </label>
          <label className="flex flex-col">
            Phone Number <span className="text-sm italic text-gray-300">For working with a peer</span>
            <input type="tel" value={vendor.phone} onChange={handleChange('phone')} required pattern="\d{3}-\d{3}-\d{4}" className="border border-gray-300 rounded px-3 py-2 mt-1" placeholder="xxx-xxx-xxxx" />
          </label>
          <label className="flex flex-col">
            Frontend GitHub URL <span className="text-sm italic text-gray-300">Your GitHub JWT Pizza frontend repository URL</span>
            <input type="url" value={vendor.gitHubUrl} onChange={handleChange('gitHubUrl')} required className="border border-gray-300 rounded px-3 py-2 mt-1" placeholder="https://github.com/youraccount/pizza" />
          </label>
          <label className="flex flex-col">
            Pizza website <span className="text-sm italic text-gray-300">Your pizza website</span>
            <input type="url" value={vendor.website} onChange={handleChange('website')} required className="border border-gray-300 rounded px-3 py-2 mt-1" placeholder="https://pizza.yourpizzawebsite.com" />
          </label>
          <div className="flex gap-2 mt-4">
            <button type="submit" className="bg-blue-600 text-white rounded px-4 py-2 font-semibold hover:bg-blue-700">
              Submit
            </button>
            <button type="button" onClick={() => setShowVendorDialog(false)} className="bg-gray-300 rounded px-4 py-2 font-semibold hover:bg-gray-400">
              Cancel
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
